'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Cookies from 'js-cookie';

import type { AviosAuthContextValue, AviosAuthState, AviosAuthUserSession } from '../core/types';
import { AuthService } from '../core/AuthService';
import { silentAuth } from '../core/silentAuth';
import {
  getCleanedUrlFromSearchParams,
  hasSilentAuthLoginRequiredError,
  hasSilentAuthSuccess,
  removeSilentAuthParams,
} from '../core/silentAuthRecovery';
import { subscribeToAuthStateChanges, synchronizeAuthState } from '../core/crossTabAuthSync';
import { isLocalDevHost } from '../core/localDevHost';
import { Logger } from '../../../lib/services/Logger';
import { stripLocale } from '../../../utilities';
import type { Accounts } from '../../../contexts/accountBalance';

import { AviosAuthContext } from './AviosAuthContext';

const LOGOUT_MARKER_STORAGE_KEY = 'avios-auth:logged-out';
const SSR_AUTH_SYNC_RELOAD_MARKER_STORAGE_KEY = 'avios-auth:ssr-auth-sync-reload';

const getCurrentLocationMarker = (): string => `${window.location.origin}${window.location.pathname}${window.location.search}${window.location.hash}`;

const markUserAsExplicitlyLoggedOut = (): void => {
  try {
    window.sessionStorage.setItem(LOGOUT_MARKER_STORAGE_KEY, getCurrentLocationMarker());
  } catch {
    // Ignore storage failures and continue with logout flow.
  }
};

const clearExplicitLogoutMarker = (): void => {
  try {
    window.sessionStorage.removeItem(LOGOUT_MARKER_STORAGE_KEY);
  } catch {
    // Ignore storage failures to avoid breaking auth flow.
  }
};

// const markSsrAuthSyncReload = (): void => {
//   try {
//     window.sessionStorage.setItem(SSR_AUTH_SYNC_RELOAD_MARKER_STORAGE_KEY, getCurrentLocationMarker());
//   } catch {
//     // Ignore storage failures and continue without reload-loop protection.
//   }
// };

const clearSsrAuthSyncReloadMarker = (): void => {
  try {
    window.sessionStorage.removeItem(SSR_AUTH_SYNC_RELOAD_MARKER_STORAGE_KEY);
  } catch {
    // Ignore storage failures.
  }
};

const hasSsrAuthSyncReloadMarker = (): boolean => {
  try {
    const marker = window.sessionStorage.getItem(SSR_AUTH_SYNC_RELOAD_MARKER_STORAGE_KEY);

    if (!marker) {
      return false;
    }

    if (marker !== getCurrentLocationMarker()) {
      clearSsrAuthSyncReloadMarker();
      return false;
    }

    return true;
  } catch {
    return false;
  }
};

const hasExplicitLogoutMarker = (): boolean => {
  try {
    const logoutMarker = window.sessionStorage.getItem(LOGOUT_MARKER_STORAGE_KEY);

    if (!logoutMarker) {
      return false;
    }

    if (logoutMarker === '1') {
      return true;
    }

    if (logoutMarker !== getCurrentLocationMarker()) {
      clearExplicitLogoutMarker();
      return false;
    }

    return true;
  } catch {
    return false;
  }
};

const clearNavigationScopedAuthMarkers = (): void => {
  clearExplicitLogoutMarker();
  clearSsrAuthSyncReloadMarker();
};

// const SILENT_AUTH_TIMEOUT_MS = 6000;
const SILENT_AUTH_DEBUG_QUERY_PARAM = 'debugSilentAuth';
const SILENT_AUTH_DEBUG_STORAGE_KEY = 'avios-auth:debug-silent-auth';
// const SILENT_AUTH_IFRAME_VISIBLE_QUERY_PARAM = 'debugSilentAuthIframe';
// const SILENT_AUTH_IFRAME_VISIBLE_STORAGE_KEY = 'avios-auth:debug-silent-auth-iframe-visible';
// const SILENT_AUTH_ALWAYS_ALLOWED_ORIGINS = [
//   'https://ba-stg.identity.stg.iagl.digital',
//   'https://ba.identity.iagl.digital',
//   'https://iberia-stg.identity.stg.iagl.digital',
//   'https://iberia.identity.iagl.digital',
//   'https://stage-accounts.aerlingus.com',
//   'https://accounts.aerlingus.com',
// ];

const isSilentAuthDebugEnabled = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    const searchParams = new URLSearchParams(window.location.search);
    const queryParamValue = searchParams.get(SILENT_AUTH_DEBUG_QUERY_PARAM);

    if (queryParamValue === '1' || queryParamValue === 'true') {
      return true;
    }

    const localStorageValue = window.localStorage.getItem(SILENT_AUTH_DEBUG_STORAGE_KEY);

    return localStorageValue === '1' || localStorageValue === 'true';
  } catch {
    return false;
  }
};

type SilentAuthDebugLogger = (message: string, payload?: unknown) => void;

const createSilentAuthDebugLogger = (): SilentAuthDebugLogger => {
  // Resolve debug mode once for the current flow to avoid repeated storage/query lookups.
  const isDebugEnabled = isSilentAuthDebugEnabled();

  return (message: string, payload?: unknown): void => {
    if (!isDebugEnabled) {
      return;
    }

    if (payload === undefined) {
      // eslint-disable-next-line no-console
      console.debug('[AviosAuth:silent]', message);
      return;
    }

    // eslint-disable-next-line no-console
    console.debug('[AviosAuth:silent]', message, payload);
  };
};

// const isSilentAuthIframeVisibleEnabled = (): boolean => {
//   if (typeof window === 'undefined') {
//     return false;
//   }

//   try {
//     const searchParams = new URLSearchParams(window.location.search);
//     const queryParamValue = searchParams.get(SILENT_AUTH_IFRAME_VISIBLE_QUERY_PARAM);

//     if (queryParamValue === '1' || queryParamValue === 'true') {
//       return true;
//     }

//     const localStorageValue = window.localStorage.getItem(SILENT_AUTH_IFRAME_VISIBLE_STORAGE_KEY);

//     return localStorageValue === '1' || localStorageValue === 'true';
//   } catch {
//     return false;
//   }
// };

// type SilentAuthIframeOutcome = { status: 'success' } | { status: 'login_required' } | { status: 'timeout' } | { status: 'error'; error: string };

// const runSilentAuthInHiddenIframe = async ({
//   opco,
//   logDebug,
//   showIframe,
// }: {
//   opco?: string;
//   logDebug: SilentAuthDebugLogger;
//   showIframe: boolean;
// }): Promise<SilentAuthIframeOutcome> => {
//   if (typeof window === 'undefined') {
//     return { status: 'error', error: 'Silent auth can only run in the browser' };
//   }

//   const silentLoginUrl = AuthService.createLoginUrl({ opco, silent: true, iframe: true });

//   logDebug('Starting hidden iframe silent auth', {
//     opco,
//     silentLoginUrl,
//   });

//   const allowedOrigins = new Set<string>([window.location.origin]);

//   try {
//     allowedOrigins.add(new URL(silentLoginUrl).origin);
//   } catch {
//     // Keep the default origin-only allow-list.
//   }

//   // Add hardcoded always-allowed origins
//   for (const origin of SILENT_AUTH_ALWAYS_ALLOWED_ORIGINS) {
//     allowedOrigins.add(origin);
//   }

//   logDebug('Added always-allowed silent auth origins', {
//     origins: Array.from(allowedOrigins),
//   });

//   return new Promise<SilentAuthIframeOutcome>((resolve) => {
//     const iframe = document.createElement('iframe');
//     const shouldShowIframe = showIframe;

//     iframe.setAttribute('aria-hidden', 'true');

//     if (shouldShowIframe) {
//       iframe.style.position = 'fixed';
//       iframe.style.right = '16px';
//       iframe.style.bottom = '16px';
//       iframe.style.width = '420px';
//       iframe.style.height = '320px';
//       iframe.style.border = '1px solid #999';
//       iframe.style.background = '#fff';
//       iframe.style.zIndex = '2147483647';
//       iframe.style.visibility = 'visible';

//       logDebug('Visible iframe debug mode enabled');
//     } else {
//       iframe.style.position = 'absolute';
//       iframe.style.width = '0';
//       iframe.style.height = '0';
//       iframe.style.border = '0';
//       iframe.style.visibility = 'hidden';
//     }

//     let isSettled = false;

//     const teardown = () => {
//       window.removeEventListener('message', onMessage);
//       window.clearTimeout(timeoutId);
//       iframe.remove();
//     };

//     const settle = (outcome: SilentAuthIframeOutcome) => {
//       if (isSettled) {
//         return;
//       }

//       logDebug('Hidden iframe flow settled', outcome);
//       isSettled = true;
//       teardown();
//       resolve(outcome);
//     };

//     const onMessage = async (event: MessageEvent) => {
//       logDebug('Observed window message event', {
//         origin: event.origin,
//         sourceMatchesIframe: event.source === iframe.contentWindow,
//         data: event.data,
//       });

//       if (!allowedOrigins.has(event.origin)) {
//         logDebug('Ignored message from disallowed origin', { origin: event.origin });
//         return;
//       }

//       if (event.source !== iframe.contentWindow) {
//         logDebug('Ignored message from unexpected source');
//         return;
//       }

//       if (!event.data || typeof event.data !== 'object') {
//         logDebug('Ignored non-object message payload');
//         return;
//       }

//       const payload = event.data as {
//         type?: string;
//         response?: { code?: string; error?: string; error_description?: string };
//         code?: string;
//         error?: string;
//         error_description?: string;
//       };

//       const response = payload.response ?? payload;
//       const messageType = payload.type ?? '';
//       const isAuthorizationMessage =
//         messageType === 'authorization_response' || typeof response.code === 'string' || typeof response.error === 'string';

//       if (!isAuthorizationMessage) {
//         logDebug('Ignored non-authorization message', payload);
//         return;
//       }

//       logDebug('Received authorization message', payload);

//       if (response.error === 'login_required') {
//         settle({ status: 'login_required' });
//         return;
//       }

//       if (typeof response.error === 'string') {
//         settle({ status: 'error', error: response.error_description ?? response.error });
//         return;
//       }

//       if (typeof response.code === 'string') {
//         try {
//           const exchangeResponse = await AuthService.exchangeSilentCode({ code: response.code, opco });

//           if (!exchangeResponse.ok) {
//             settle({
//               status: 'error',
//               error: `Silent code exchange failed with status ${exchangeResponse.status}`,
//             });
//             return;
//           }

//           settle({ status: 'success' });
//           return;
//         } catch (error) {
//           settle({
//             status: 'error',
//             error: error instanceof Error ? error.message : 'Silent code exchange failed',
//           });
//           return;
//         }
//       }

//       settle({
//         status: 'error',
//         error: 'Authorization response did not include a code',
//       });
//     };

//     const timeoutId = window.setTimeout(() => {
//       settle({ status: 'timeout' });
//     }, SILENT_AUTH_TIMEOUT_MS);

//     window.addEventListener('message', onMessage);
//     document.body.appendChild(iframe);
//     logDebug('Navigating hidden iframe', {
//       silentLoginUrl,
//       allowedOrigins: Array.from(allowedOrigins),
//     });
//     iframe.src = silentLoginUrl;
//   });
// };

type SilentAuthRecoveryOutcome = 'none' | 'login_required_error' | 'success';

type SilentAuthRecoverySource =
  | {
      source: 'search';
      searchParams: URLSearchParams;
    }
  | {
      source: 'hash';
      hashPrefix: string;
      hashSearchParams: URLSearchParams;
    };

const getSilentAuthRecoverySource = (): SilentAuthRecoverySource | null => {
  const searchParams = new URLSearchParams(window.location.search);

  if (hasSilentAuthLoginRequiredError(searchParams) || hasSilentAuthSuccess(searchParams)) {
    return {
      source: 'search',
      searchParams,
    };
  }

  const hashContent = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : window.location.hash;
  const hashQueryIndex = hashContent.indexOf('?');

  if (hashQueryIndex === -1) {
    return null;
  }

  const hashPrefix = hashContent.slice(0, hashQueryIndex);
  const hashQuery = hashContent.slice(hashQueryIndex + 1);
  const hashSearchParams = new URLSearchParams(hashQuery);

  if (!hasSilentAuthLoginRequiredError(hashSearchParams) && !hasSilentAuthSuccess(hashSearchParams)) {
    return null;
  }

  return {
    source: 'hash',
    hashPrefix,
    hashSearchParams,
  };
};

const getHashWithCleanedSilentAuthParams = ({ hashPrefix, hashSearchParams }: { hashPrefix: string; hashSearchParams: URLSearchParams }): string => {
  const cleanedHashSearchParams = removeSilentAuthParams(hashSearchParams);
  const cleanedHashQuery = cleanedHashSearchParams.toString();
  const cleanedHashContent = `${hashPrefix}${cleanedHashQuery ? `?${cleanedHashQuery}` : ''}`;

  return cleanedHashContent ? `#${cleanedHashContent}` : '';
};

const consumeSilentAuthRecoveryOutcome = (): SilentAuthRecoveryOutcome => {
  const recoverySource = getSilentAuthRecoverySource();

  if (!recoverySource) {
    return 'none';
  }

  const searchParams = recoverySource.source === 'search' ? recoverySource.searchParams : recoverySource.hashSearchParams;
  const hasLoginRequiredRecoveryError = hasSilentAuthLoginRequiredError(searchParams);

  if (hasLoginRequiredRecoveryError) {
    const nextUrl =
      recoverySource.source === 'search'
        ? getCleanedUrlFromSearchParams({
            pathname: window.location.pathname,
            hash: window.location.hash,
            searchParams,
          })
        : `${window.location.pathname}${window.location.search}${getHashWithCleanedSilentAuthParams({
            hashPrefix: recoverySource.hashPrefix,
            hashSearchParams: searchParams,
          })}`;

    window.history.replaceState(window.history.state, document.title, nextUrl);

    // Hash-based callbacks can appear after a full-page round-trip where a
    // first-party session may still be valid. Clean the URL but continue with
    // the normal session fetch path instead of hard-failing to logged-out.
    if (recoverySource.source === 'hash') {
      return 'none';
    }

    return 'login_required_error';
  }

  const hasLoginSuccess = hasSilentAuthSuccess(searchParams);

  if (!hasLoginSuccess) {
    return 'none';
  }

  const nextUrl =
    recoverySource.source === 'search'
      ? getCleanedUrlFromSearchParams({
          pathname: window.location.pathname,
          hash: window.location.hash,
          searchParams,
        })
      : `${window.location.pathname}${window.location.search}${getHashWithCleanedSilentAuthParams({
          hashPrefix: recoverySource.hashPrefix,
          hashSearchParams: searchParams,
        })}`;

  window.history.replaceState(window.history.state, document.title, nextUrl);

  return 'success';
};

// const isSafari = (): boolean => {
//   if (typeof navigator === 'undefined') {
//     return false;
//   }

//   const ua = navigator.userAgent;

//   // Safari includes "Safari" but so do Chromium browsers — exclude those.
//   return /Safari/.test(ua) && !/Chrome|Chromium|CriOS|Edg|OPR/.test(ua);
// };

// const isBrave = async (): Promise<boolean> => {
//   if (typeof navigator === 'undefined') {
//     return false;
//   }

//   const nav = navigator as Navigator & { brave?: { isBrave: () => Promise<boolean> } };

//   try {
//     return (await nav.brave?.isBrave()) ?? false;
//   } catch {
//     return false;
//   }
// };

// const isIOS = (): boolean =>
//   /iPad|iPhone|iPod/.test(navigator.userAgent) ||
//   // iPadOS 13+ reports as macOS but has touch support
//   (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

// /**
//  * Detects Chrome/Chromium with third-party cookies blocked.
//  *
//  * Uses the storage-quota heuristic: Chrome incognito caps
//  * `navigator.storage.estimate()` quota at ~120 MB, whereas a normal
//  * Chrome profile has several GB available. This covers the incognito
//  * case but not normal-mode "Block third-party cookies" toggle or
//  * privacy extensions (those are handled reactively).
//  */
// const isChromiumWithBlockedThirdPartyCookies = async (): Promise<boolean> => {
//   if (typeof navigator === 'undefined' || !navigator.storage?.estimate) {
//     return false;
//   }

//   const ua = navigator.userAgent;
//   // Target Chrome/Chromium only; exclude Edge (Edg) and Opera (OPR).
//   const isChrome = /Chrome|CriOS/.test(ua) && !/Edg|OPR/.test(ua);

//   if (!isChrome) {
//     return false;
//   }

//   try {
//     const { quota } = await navigator.storage.estimate();
//     // Normal Chrome profiles report a quota that's a large fraction of free
//     // disk space (typically tens/hundreds of GB). Incognito is capped much
//     // lower. The threshold is a judgment call.
//     return typeof quota === 'number' && quota < 2 * 1024 * 1024 * 1024 * 2; // < ~4GB
//   } catch {
//     return false;
//   }
// };

// /**
//  * Browsers that block third-party cookies inside iframes (Safari ITP,
//  * Brave Shields, Chrome incognito), which breaks the hidden-iframe
//  * silent auth flow.
//  *
//  * Note: Normal-mode Chrome with "Block third-party cookies" enabled or
//  * privacy extensions also blocks these cookies but cannot be detected
//  * up front. Those contexts are handled reactively instead: any
//  * non-success iframe outcome (login_required / timeout / error)
//  * triggers a direct first-party session check before the user is
//  * treated as logged out.
//  */
// const blocksThirdPartyIframeCookies = async (): Promise<boolean> => {
//   return isSafari() || (await isBrave()) || isIOS() || (await isChromiumWithBlockedThirdPartyCookies());
// };

/**
 * Finds a first-party session id cookie. Prefers the opco-derived name,
 * but falls back to scanning all cookies for any `*_session_id` key so
 * the direct session check still works when AVIOS_OPCO is absent
 * (e.g. right after a post-login redirect where only the session cookie
 * was set by the cookie manager).
 */
const findSessionIdCookie = (opco?: string): string | undefined => {
  if (opco) {
    const sessionId = Cookies.get(`${opco.toLowerCase()}_session_id`);

    if (sessionId) {
      return sessionId;
    }
  }

  const allCookies = Cookies.get();
  const sessionCookieName = Object.keys(allCookies).find((name) => name.endsWith('_session_id') && name.length > '_session_id'.length);

  return sessionCookieName ? allCookies[sessionCookieName] : undefined;
};

export interface AviosAuthProviderProps {
  /**
   * Optional user pre-fetched via SSR. If provided, the provider assumes
   * the page was rendered on the server and hydrates with this user.
   * No loading spinner will show, and no additional fetch is triggered.
   */
  initialUser?: AviosAuthUserSession | null;

  // /**
  //  * Whether to reload the page when silent auth state changes.
  //  * Applies to both successful silent authentication and login_required outcomes.
  //  * - Set to `true` for SSR apps (e.g., Next.js) that need a fresh server render after silent auth reconciliation
  //  * - Set to `false` or omit for SPAs that fetch user data client-side after state update
  //  * @default false
  //  */
  // reloadPageOnSilentAuthChange?: boolean;

  /** Optional relative path override for the returnTo URL after login. If not provided, defaults to the current page URL. */
  loginReturnTo?: string;

  /** Optional relative path override for the returnTo URL after logout. If not provided, defaults to the current page URL. */
  logoutReturnTo?: string;

  /** Optional preloaded balance used instead of fetching the balance endpoint. */
  initialBalance?: Accounts;

  /** Optional hostnames or URLs that should be treated as local dev hosts (e.g. hosts file mappings). */
  localDevHostPatterns?: string[];

  /** Whether to enable silent auth redirect when no user is found. If enabled, the page will redirect to the silent login URL once per location. */
  // enableSilentAuthRedirect?: boolean;

  /**
   * Optional explicit origin for the Avios auth gateway (e.g. `https://tst.avios.com`).
   * By default all auth requests/redirects use the current page's own origin, which
   * relies on upstream infra (CDN/ALB) to proxy `/auth-gateway/*` and `/shell/api/*` to
   * the real gateway. Set this when the app's domain does not have that routing, so
   * calls target the real gateway origin directly instead of looping back into the app.
   */
  gatewayUrl?: string;

  /** React children to render within the provider */
  children: React.ReactNode;
}

export const AviosAuthProvider = ({
  children,
  initialUser,
  // enabled,
  // reloadPageOnSilentAuthChange = false,
  loginReturnTo,
  logoutReturnTo,
  initialBalance,
  localDevHostPatterns,
  // enableSilentAuthRedirect = false,
  gatewayUrl,
}: AviosAuthProviderProps) => {
  const attemptedSilentAuthRef = useRef(false);
  const hadInitialUser = useRef(initialUser !== undefined);

  /**
   * Tells other tabs this tab's auth state changed, so a tab left on a
   * different OpCo's session can resync. This tab doesn't reload itself, it
   * already applied the fresh user via setState right before this runs, so
   * reloading would be redundant. It used to reload itself too, but
   * that reload fired every time this tab's own state looked different from
   * what it saw last, with nothing stopping it from repeating. In Safari,
   * the AVIOS_OPCO cookie isn't always readable straight after the login
   * redirect, so that check could flip back and forth and reload forever.
   */
  const synchronizeAuthStateAcrossTabs = useCallback((user: AviosAuthUserSession | null | undefined) => {
    synchronizeAuthState(user);
  }, []);

  /**
   * Deduplicates concurrent refreshAuth invocations (e.g. React StrictMode
   * double-invoking the mount effect). While a refresh is in flight, later
   * callers await the same promise instead of prematurely flipping
   * `isLoading` to false — which previously exposed a transient
   * `{ isLoading: false, isAuthenticated: false }` state that triggered
   * consumer auto-login redirects and caused a login loop.
   */
  const inFlightRefreshRef = useRef<Promise<AviosAuthUserSession | null> | null>(null);

  const [state, setState] = useState<AviosAuthState>(() => ({
    user: initialUser ?? null,
    isLoading: !hadInitialUser.current,
    isAuthenticated: !!initialUser,
    error: null,
  }));

  const setUnauthenticatedState = useCallback((error: string | null = null) => {
    setState({
      user: null,
      isLoading: false,
      isAuthenticated: false,
      error,
    });
  }, []);

  // ── Silent auth check ──

  const refreshAuth = useCallback((): Promise<AviosAuthUserSession | null> => {
    // If a refresh is already running, join it. Crucially, do NOT touch
    // state here — the in-flight run owns the loading flag.
    if (inFlightRefreshRef.current) {
      return inFlightRefreshRef.current;
    }

    // Already completed for this page load; state is final. Nothing to do.
    if (attemptedSilentAuthRef.current) {
      return Promise.resolve(null);
    }

    const run = async (): Promise<AviosAuthUserSession | null> => {
      const debugLog = createSilentAuthDebugLogger();
      // const isSilentAuthIframeVisible = isSilentAuthIframeVisibleEnabled();

      // If we just reloaded after detecting login_required, the SSR user is stale.
      // Clear it and the marker so we don't loop.
      if (hasSsrAuthSyncReloadMarker()) {
        debugLog('Detected SSR sync reload marker: clearing stale SSR user');
        clearSsrAuthSyncReloadMarker();
        attemptedSilentAuthRef.current = true;

        setUnauthenticatedState();
        synchronizeAuthStateAcrossTabs(null);

        return null;
      }

      if (hasExplicitLogoutMarker()) {
        debugLog('Skipping refreshAuth due to explicit logout marker');
        // Consume the marker once so only the immediate post-logout load is suppressed.
        clearExplicitLogoutMarker();
        attemptedSilentAuthRef.current = true;

        setUnauthenticatedState();
        synchronizeAuthStateAcrossTabs(null);

        return null;
      }

      attemptedSilentAuthRef.current = true;
      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      debugLog('refreshAuth started', {
        hadInitialUser: hadInitialUser.current,
        // enabled,
      });

      try {
        const recoveryOutcome = consumeSilentAuthRecoveryOutcome();

        if (recoveryOutcome === 'login_required_error') {
          setUnauthenticatedState();
          synchronizeAuthStateAcrossTabs(null);

          return null;
        }

        const opco = Cookies.get('AVIOS_OPCO');
        const sessionId = findSessionIdCookie(opco);
        const isLocalhost = isLocalDevHost(window.location.hostname, localDevHostPatterns);
        // const skipIframeSilentAuth = isLocalhost || (await blocksThirdPartyIframeCookies());

        if (recoveryOutcome === 'success') {
          const user = await silentAuth({ sessionId, isLocalhost, gatewayUrl: gatewayUrl ?? window.location.origin });
          setState({
            user: user,
            isLoading: false,
            isAuthenticated: !!user,
            error: null,
          });
          synchronizeAuthStateAcrossTabs(user);
          return user;
        }

        // Always attempt a session fetch regardless of whether a JS-readable
        // session cookie was found. httpOnly cookies are invisible to js-cookie
        // but the browser automatically includes them in same-origin fetch
        // requests, so the server can still validate the session.
        // `sessionId` is passed as an optimisation hint when available.
        debugLog('Attempting session fetch', { hasJsReadableSessionId: !!sessionId });

        try {
          const user = await silentAuth({ sessionId, isLocalhost, gatewayUrl: gatewayUrl ?? window.location.origin });

          if (user) {
            setState({
              user,
              isLoading: false,
              isAuthenticated: true,
              error: null,
            });

            debugLog('Session fetch succeeded', { hasUser: true });
            synchronizeAuthStateAcrossTabs(user);
            return user;
          }

          debugLog('Silent login redirect already attempted for this location; treating as unauthenticated');
        } catch (err) {
          Logger.error('Session check failed', err as Error);
        }

        setUnauthenticatedState();
        synchronizeAuthStateAcrossTabs(null);
        return null;

        // if (skipIframeSilentAuth) {
        //   debugLog('Skipping iframe silent auth on local dev host or third-party cookie blocking browser; attempting direct session fetch');

        //   try {
        //     const user = await silentAuth({ sessionId, isLocalhost, gatewayUrl: window.location.origin });

        //     setState({
        //       user: user ?? null,
        //       isLoading: false,
        //       isAuthenticated: !!user,
        //       error: null,
        //     });

        //     return user;
        //   } catch (err) {
        //     const error = err instanceof Error ? err.message : 'Local auth check failed';
        //     Logger.error('Error during localhost auth check', err as Error);
        //     setUnauthenticatedState(error);
        //     return null;
        //   }
        // }

        // const iframeOutcome = await runSilentAuthInHiddenIframe({ opco, logDebug: debugLog, showIframe: isSilentAuthIframeVisible });
        // debugLog('Final iframe outcome status', {
        //   status: iframeOutcome.status,
        // });

        // if (iframeOutcome.status === 'success') {
        //   debugLog('Iframe success', { reloadPageOnSilentAuthChange });

        //   if (reloadPageOnSilentAuthChange && !hadInitialUser.current) {
        //     debugLog('Reloading page for SSR server render');
        //     window.location.reload();
        //     return null;
        //   }

        //   // For SPAs: fetch user after session cookie is set by backend code exchange
        //   debugLog('Fetching user after silent auth (SPA mode)');
        //   const refreshedSessionId = findSessionIdCookie(Cookies.get('AVIOS_OPCO') ?? opco);
        //   const user = await silentAuth({
        //     sessionId: refreshedSessionId ?? sessionId,
        //     isLocalhost,
        //     gatewayUrl: window.location.origin,
        //   });

        //   setState({
        //     user: user ?? null,
        //     isLoading: false,
        //     isAuthenticated: !!user,
        //     error: null,
        //   });

        //   debugLog('Silent auth completed', { hasUser: !!user });
        //   return user;
        // }

        // if (iframeOutcome.status === 'error') {
        //   debugLog('Iframe error outcome', { error: iframeOutcome.error });
        //   Logger.error('Silent auth iframe failed', new Error(iframeOutcome.error));
        // }

        // if (iframeOutcome.status === 'timeout') {
        //   debugLog('Iframe timeout');
        //   Logger.error('Silent auth iframe timed out');
        // }
        // // if (iframeOutcome.status === 'timeout' || iframeOutcome.status === 'error') {
        // //   debugLog('Iframe flow failed; falling back to direct session fetch', iframeOutcome);

        // //   try {
        // //     const user = await silentAuth({ sessionId, isLocalhost, gatewayUrl: window.location.origin });

        // //     setState({
        // //       user: user ?? null,
        // //       isLoading: false,
        // //       isAuthenticated: !!user,
        // //       error: null,
        // //     });

        // //     return user;
        // //   } catch (err) {
        // //     Logger.error('Fallback auth check failed', err as Error);
        // //     setUnauthenticatedState(err instanceof Error ? err.message : 'Auth check failed');
        // //     return null;
        // //   }
        // // }

        // if (iframeOutcome.status === 'login_required') {
        //   debugLog('Iframe login_required outcome', {
        //     reloadPageOnSilentAuthChange,
        //     hadInitialUser: hadInitialUser.current,
        //     hasSsrMarker: hasSsrAuthSyncReloadMarker(),
        //   });

        //   // In third-party-cookie-blocked contexts (Chrome incognito,
        //   // Chrome with "Block third-party cookies" enabled, privacy
        //   // extensions), the iframe cannot see the IdP cookies, so
        //   // login_required does NOT mean the user is logged out — it means
        //   // the iframe was blind. Verify the first-party gateway session
        //   // directly before treating this as a genuine logout. Note this
        //   // must run even when `sessionId` is undefined: the gateway
        //   // session cookie may be httpOnly (unreadable by js-cookie) but
        //   // still rides along on the fetch automatically.
        //   // debugLog('Verifying first-party session before treating login_required as a logout');

        //   // try {
        //   //   const user = await silentAuth({ sessionId, isLocalhost, gatewayUrl: window.location.origin });

        //   //   if (user) {
        //   //     debugLog('First-party session still valid; ignoring login_required (third-party cookies likely blocked)');

        //   //     setState({
        //   //       user,
        //   //       isLoading: false,
        //   //       isAuthenticated: true,
        //   //       error: null,
        //   //     });

        //   //     return user;
        //   //   }

        //   //   debugLog('First-party session invalid or absent; treating login_required as a genuine logout');
        //   // } catch (err) {
        //   //   Logger.error('First-party session verification failed after login_required', err as Error);
        //   // }

        //   if (reloadPageOnSilentAuthChange && hadInitialUser.current && !hasSsrAuthSyncReloadMarker()) {
        //     debugLog('Reloading page to reconcile SSR state after login_required');

        //     // Clear session cookies and wait for response to ensure Set-Cookie headers are processed
        //     try {
        //       await AuthService.clearAuthSessionCookies({ localDevHostPatterns });
        //       debugLog('Auth session cookies cleared');
        //     } catch (err) {
        //       Logger.error('Error clearing auth session', err as Error);
        //       // Continue with reload anyway
        //     }

        //     // Mark reload and reload page
        //     markSsrAuthSyncReload();
        //     window.location.reload();
        //     return null;
        //   }
        // }

        // setUnauthenticatedState();
        // debugLog('refreshAuth finished - user is null/unauthenticated');
        // return null;
      } catch (err) {
        const error = err instanceof Error ? err.message : 'Auth check failed';
        Logger.error('Error during silent auth check', err as Error);
        setUnauthenticatedState(error);
        synchronizeAuthStateAcrossTabs(null);
        return null;
      }
    };

    const promise = run().finally(() => {
      inFlightRefreshRef.current = null;
    });

    inFlightRefreshRef.current = promise;

    return promise;
  }, [gatewayUrl, localDevHostPatterns, setUnauthenticatedState, synchronizeAuthStateAcrossTabs]);

  // ── Mount: silent check if no SSR user provided ──

  useEffect(() => {
    const debugLog = createSilentAuthDebugLogger();

    debugLog('AviosAuthProvider mount/update effect', {
      hadInitialUser: hadInitialUser.current,
    });

    if (hadInitialUser.current) {
      consumeSilentAuthRecoveryOutcome();
      debugLog('Skipping refreshAuth from effect because an initialUser was provided');
      return;
    }

    // if (!enabled) {
    //   debugLog('Skipping refreshAuth from effect because auth is disabled');
    //   setState((prev) => ({ ...prev, isLoading: false }));
    //   return;
    // }

    debugLog('Triggering refreshAuth from effect on page load', {
      hadInitialUser: hadInitialUser.current,
    });

    refreshAuth();
  }, [refreshAuth]);

  useEffect(() => {
    if (initialUser !== undefined) {
      setState({
        user: initialUser,
        isLoading: false,
        isAuthenticated: !!initialUser,
        error: null,
      });
      synchronizeAuthStateAcrossTabs(initialUser);
    }
  }, [initialUser, synchronizeAuthStateAcrossTabs]);

  useEffect(
    () =>
      subscribeToAuthStateChanges(({ locale }) => {
        const currentPath = window.location.pathname;
        const strippedPath = stripLocale(currentPath);
        const targetPath = locale && strippedPath !== currentPath ? `/${locale}${strippedPath}` : currentPath;

        window.location.href = `${window.location.origin}${targetPath}${window.location.search}${window.location.hash}`;
      }),
    []
  );

  useEffect(() => {
    let lastKnownLocationMarker = getCurrentLocationMarker();

    const clearMarkersIfLocationChanged = (): void => {
      const nextLocationMarker = getCurrentLocationMarker();

      if (nextLocationMarker !== lastKnownLocationMarker) {
        clearNavigationScopedAuthMarkers();
        lastKnownLocationMarker = nextLocationMarker;
      }
    };

    window.addEventListener('popstate', clearMarkersIfLocationChanged);
    window.addEventListener('hashchange', clearMarkersIfLocationChanged);

    return () => {
      window.removeEventListener('popstate', clearMarkersIfLocationChanged);
      window.removeEventListener('hashchange', clearMarkersIfLocationChanged);
    };
  }, []);

  // ── Login ──

  const login = useCallback(
    async ({ opcoId, useOnClick, returnTo }: { opcoId?: string; useOnClick?: boolean; returnTo?: string }) => {
      clearExplicitLogoutMarker();
      attemptedSilentAuthRef.current = false;
      setState((prev) => ({ ...prev, isLoading: true, error: null }));
      if (useOnClick) {
        try {
          const res = await AuthService.login({
            opco: opcoId,
            overrideReturnTo: returnTo ?? loginReturnTo,
            localDevHostPatterns,
            gatewayUrl,
          });
          const data = await res.json();
          if (res.ok && data) {
            if (data.location) {
              window.location.href = data.location;
            } else {
              Logger.error('No redirect location provided');
              setState((prev) => ({ ...prev, isLoading: false, error: 'No redirect location provided' }));
            }
          } else {
            Logger.error('Login failed');
            setState((prev) => ({ ...prev, isLoading: false, error: 'Login failed' }));
          }
        } catch (err) {
          const error = err instanceof Error ? err.message : 'Error during Login';
          Logger.error('Error during Login', err as Error);
          setState((prev) => ({ ...prev, isLoading: false, error }));
        }
      } else {
        window.location.href = AuthService.createLoginUrl({
          opco: opcoId,
          overrideReturnTo: returnTo ?? loginReturnTo,
          localDevHostPatterns,
          gatewayUrl,
        });
      }
    },
    [gatewayUrl, localDevHostPatterns, loginReturnTo]
  );

  // ── Logout ──

  const logout = useCallback(
    async ({ useOnClick, returnTo }: { useOnClick?: boolean; returnTo?: string } = {}) => {
      markUserAsExplicitlyLoggedOut();
      setState((prev) => ({ ...prev, isLoading: true, error: null }));
      if (useOnClick) {
        try {
          const authResponse = await AuthService.logout({
            overrideReturnTo: returnTo ?? logoutReturnTo,
            localDevHostPatterns,
            gatewayUrl,
          });
          const data = await authResponse.json();
          if (authResponse.ok && data) {
            if (data.location) {
              window.location.href = data.location;
            } else {
              Logger.error('No redirect location provided');
              setState((prev) => ({ ...prev, isLoading: false, error: 'No redirect location provided' }));
            }
          } else {
            Logger.error('Logout failed');
            setState((prev) => ({ ...prev, isLoading: false, error: 'Logout failed' }));
          }
        } catch (err) {
          const error = err instanceof Error ? err.message : 'Error during logout';
          Logger.error('Error during logout', err as Error);
          setState((prev) => ({ ...prev, isLoading: false, error }));
        }
      } else {
        window.location.href = AuthService.createLogoutUrl({
          overrideReturnTo: returnTo ?? logoutReturnTo,
          localDevHostPatterns,
          gatewayUrl,
        });
      }
    },
    [gatewayUrl, localDevHostPatterns, logoutReturnTo]
  );

  // -- Balance --

  const getBalance = useCallback(async () => {
    if (!state.isAuthenticated || !state.user) {
      return null;
    }

    if (initialBalance) {
      return initialBalance;
    }

    try {
      const balanceResponse = await AuthService.getBalance({ localDevHostPatterns, gatewayUrl });
      if (balanceResponse.ok) {
        const balanceData = await balanceResponse.json();
        return balanceData;
      } else {
        Logger.error('Failed to fetch balance');
        return null;
      }
    } catch (err) {
      Logger.error('Error fetching balance', err as Error);
      return null;
    }
  }, [gatewayUrl, initialBalance, localDevHostPatterns, state.isAuthenticated, state.user]);

  // ── URL helpers ──

  const getLoginUrl = useCallback(
    (params?: { opcoId?: string; returnTo?: string }) =>
      AuthService.createLoginUrl({
        opco: params?.opcoId,
        overrideReturnTo: params?.returnTo ?? loginReturnTo,
        localDevHostPatterns,
        gatewayUrl,
      }),
    [gatewayUrl, localDevHostPatterns, loginReturnTo]
  );

  const getLogoutUrl = useCallback(
    (params?: { returnTo?: string }) =>
      AuthService.createLogoutUrl({
        overrideReturnTo: params?.returnTo ?? logoutReturnTo,
        localDevHostPatterns,
        gatewayUrl,
      }),
    [gatewayUrl, localDevHostPatterns, logoutReturnTo]
  );

  // ── Context value ──

  const value = useMemo<AviosAuthContextValue>(
    () => ({ ...state, login, logout, getLoginUrl, getLogoutUrl, getBalance }),
    [state, login, logout, getLoginUrl, getLogoutUrl, getBalance]
  );

  return <AviosAuthContext.Provider value={value}>{children}</AviosAuthContext.Provider>;
};
